import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import EventBanner from "@/components/EventBanner";
import WizardPage from "@/pages/WizardPage";
import Manifesto from "@/components/Manifesto";
import Marquee from "@/components/Marquee";
import Expertises from "@/components/Expertises";
import Offres from "@/components/Offres";
import Team from "@/components/Team";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import Ressources from "@/pages/Ressources";
import APropos from "@/pages/APropos";
import FicheDetail from "@/pages/FicheDetail";
import SharedPage from "@/pages/SharedPage";
import Compare from "@/pages/Compare";
import Profil from "@/pages/Profil";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminFiches from "@/pages/admin/AdminFiches";
import AdminFicheEdit from "@/pages/admin/AdminFicheEdit";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminMedias from "@/pages/admin/AdminMedias";
import AdminAccount from "@/pages/admin/AdminAccount";
import Legal, { AiProcessingSection } from "@/pages/Legal";
import { LanguageProvider, useTranslation } from "@/i18n";
import legacyRedirects from "@/data/legacyRedirects.json";
import { getToken } from "@/adminAuth";

// Phase 1 (vitrine) : le portail Ressources/Wizard n'est visible qu'aux membres du cabinet (token admin).
const AdminOnly = ({ children }) => (getToken() ? children : <Navigate to="/" replace />);

const LEGACY_SECTIONS = ["legalconcepts", "clauses", "laws", "proceedings", "document", "needs", "enforceablerights", "company", "player", "compliances", "faqs", "topics", "categories", "author"];

const LegacyRedirect = () => {
  const { pathname } = useLocation();
  const key = pathname.toLowerCase().replace(/\.html$/, "").replace(/\/+$/, "");
  if (legacyRedirects[key]) return <Navigate to={legacyRedirects[key]} replace />;
  const parts = key.split("/").filter(Boolean);
  if ((parts[0] === "fr" || parts[0] === "en" || parts[0] === "nl") && (LEGACY_SECTIONS.includes(parts[1]) || /^20\d\d$/.test(parts[1] || ""))) {
    return <Navigate to="/ressources" replace />;
  }
  return <Navigate to="/" replace />;
};

const Seo = () => {
  const { lang, t } = useTranslation();
  useEffect(() => {
    document.title = t("seo.title");
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("seo.description"));
  }, [lang, t]);
  return null;
};

const Home = () => (
  <>
    <Seo />
    <Hero />
    <EventBanner />
    <Marquee />
    <Manifesto />
    <Expertises />
    <Offres />
    <Team />
    <Contact />
  </>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.085, smoothWheel: true });
    window.__lenis = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  return (
    <div className="grain bg-ink min-h-screen text-[#FAF8F5]" data-testid="app-root">
      <LanguageProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/orientation" element={<AdminOnly><WizardPage /></AdminOnly>} />
            <Route path="/ressources" element={<AdminOnly><Ressources /></AdminOnly>} />
            <Route path="/ressources/a-propos" element={<AdminOnly><APropos /></AdminOnly>} />
            <Route path="/comparer" element={<AdminOnly><Compare /></AdminOnly>} />
            <Route path="/ressources/:type/:slug" element={<FicheDetail />} />
            <Route path="/partage/:key" element={<SharedPage />} />
            <Route path="/equipe/:slug" element={<Profil />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reinitialiser" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminFiches />} />
            <Route path="/admin/fiches/:type/:slug" element={<AdminFicheEdit />} />
            <Route path="/admin/equipe" element={<AdminTeam />} />
            <Route path="/admin/medias" element={<AdminMedias />} />
            <Route path="/admin/compte" element={<AdminAccount />} />
            <Route path="/conditions-utilisation" element={<Legal slug="terms-of-use" title="Conditions d'utilisation" />} />
            <Route path="/conditions-utilisation-portail" element={<Legal slug="knowledge-portal-terms-conditions" title="Conditions d'utilisation de la base de connaissance" />} />
            <Route path="/vie-privee" element={<Legal slug="privacy-policy" title="Vie privée" extra={<AiProcessingSection />} />} />
            <Route path="*" element={<LegacyRedirect />} />
          </Routes>
        </main>
        <Footer />
        <CookieBanner />
      </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
